import { Database, GitBranch, Network, Table2, Workflow } from "lucide-react";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { ROUTES } from "@/routes";
import { cn } from "@/lib/utils";

const DATABASE_NAV = [
  { key: "overview", label: "Overview", icon: Database, path: "" },
  { key: "schemas", label: "Schema Visualizer", icon: Network, path: "schemas" },
  { key: "tables", label: "Tables", icon: Table2, path: "tables" },
  { key: "functions", label: "Database Functions", icon: Workflow, path: "functions" },
  { key: "triggers", label: "Database Triggers", icon: GitBranch, path: "triggers" },
] as const;

/** Shared navigation shell for the database tools under a project. */
export function DatabaseLayout() {
  const { id = "" } = useParams<{ id: string }>();
  const base = ROUTES.database(id);

  return (
    <div className="space-y-6">
      <div className="flex min-w-0 flex-col gap-3 border-b border-border-standard pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-secondary">
            <Database className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="eyebrow">Project database</p>
            <p className="truncate text-sm text-text-muted">
              Schema, tables, functions, and triggers
            </p>
          </div>
        </div>
        <nav
          className="flex min-w-0 gap-1 overflow-x-auto pb-0.5"
          aria-label="Database navigation"
        >
          {DATABASE_NAV.map(({ key, label, icon: Icon, path }) => (
            <NavLink
              key={key}
              to={path ? `${base}/${path}` : base}
              end={path === ""}
              className={({ isActive }) =>
                cn(
                  "inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-sm px-3 text-xs font-semibold transition-colors duration-fast ease-standard",
                  isActive
                    ? "bg-secondary-fixed text-on-secondary-fixed-variant"
                    : "text-text-muted hover:bg-background-subtle hover:text-text-primary",
                )
              }
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
      <Outlet />
    </div>
  );
}
